import { Button, Form, Input, Modal, notification, Pagination, Rate, Skeleton, Spin, Table, Tag } from 'antd';
import { useEffect, useState } from 'react';
import feedbackService from '../../../services/feedback-service';
import dayjs from 'dayjs';
import { FEEDBACK_STATUS } from '../../../constants';
import { changeDocumentTitle } from '../../../utils/title';
import { SearchOutlined } from "@ant-design/icons";

function AdminFeedback() {
    const [form] = Form.useForm()
    const [feedback, setFeedback] = useState([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState({ current: 1, total: 0, pageSize: 10, });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showModal, setShowModal] = useState(false)
    const [search, setSearch] = useState(null)
    const [statusFilter, setStatusFilter] = useState('All'); // 'All', 'Open', 'Resolved'
    const [cardDetail, setCardDetail] = useState([])
    const handleViewDetails = (record) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
    }

    const handleResolve = (record) => {
        setSelectedRecord(record);
        setShowModal(true)
    }

    const formatSize = (sizeInBytes) => {
        if (sizeInBytes >= 1024 * 1024) return (sizeInBytes / (1024 * 1024)).toFixed(2) + " MB";
        if (sizeInBytes >= 1024) return (sizeInBytes / 1024).toFixed(2) + " KB";
        return sizeInBytes + " B";
    }

    const isImage = (filename) => /\.(jpeg|jpg|gif|png|webp)$/i.test(filename);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const data = { resolved_message: values.resolved_message }
            const response = await feedbackService.resolve(selectedRecord.id, data);
            setLoading(false);
            initialize()
            notification.success({ message: 'Feedback resolved successfully' });
            setShowModal(false);
            form.resetFields();
        }
        catch (error) {
            notification.error({
                message: 'Failed to resolve feedback',
                description: error?.response?.data?.message || error.message || 'Please try again later.',
            });
        }
    };

    const cardData = [
        { title: 'Total', count: cardDetail.total },
        { title: 'Open', count: cardDetail.open },
        { title: 'Resolved', count: cardDetail.resolved },
        { title: 'Average rating', count: cardDetail.average_rating, rating: "Out of 5" },
    ];
    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            render: (text) => <span className="font-semibold">FB-{text}</span>,
        },
        {
            title: "Customer",
            dataIndex: "name",
            key: "name",
            render: (_, record) => (
                <div>
                    <p className="m-0 font-medium">{record.name}</p>
                    <p className="m-0 text-gray-500 text-xs">{record.email}</p>
                </div>
            )
        },
        {
            title: "Rating",
            dataIndex: "rating",
            key: "rating",
            render: (rating) => (
                <Rate value={rating} disabled style={{ fontSize: 15 }} />
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text, record) => (
                <Tag className='uppercase' color={record.status === FEEDBACK_STATUS.RESOLVED ? "green" : "orange"}>{record.status}</Tag>
            ),
        },
        {
            title: "Category",
            dataIndex: "area_of_feedbacks",
            key: "area_of_feedbacks",
            render: (area_of_feedbacks) => (
                <div style={{ display: "flex", flexWrap: "nowrap", gap: "8px", alignItems: "center" }}>
                    {Array.isArray(area_of_feedbacks)
                        ? area_of_feedbacks.map((item, index) => (
                            <Tag color="red" key={index} style={{ margin: 0 }}>
                                {item}
                            </Tag>
                        ))
                        : <Tag color="red" style={{ margin: 0 }}>{area_of_feedbacks}</Tag>}
                </div>
            ),
        },
        {
            title: "Summary",
            dataIndex: "feedback",
            key: "feedback",
            width: 100,
            render: (text, record) => (
                <div>
                    <p className="truncate" style={{ margin: 0, maxWidth: 180 }}>
                        {record.feedback}
                    </p>
                    <Button type="link" size="small" onClick={() => handleViewDetails(record)}>
                        View Details
                    </Button>
                </div>
            )
        },
        {
            title: "Received",
            dataIndex: "created_at",
            key: "created_at",
            render: (created_at) => (
                <span>{dayjs(created_at).format("YYYY-MM-DD HH:mm")}</span>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <div className="flex gap-2">
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => handleResolve(record)}
                        disabled={record.status === FEEDBACK_STATUS.RESOLVED}
                    >
                        Resolve
                    </Button>
                </div>
            ),
        },
    ];

    const initialize = async (page = 1, pageSize = pagination.pageSize, search = null, status = 'All') => {
        setLoading(true);
        const statusParam = status === 'All' ? null : status;
        const { success, data } = await feedbackService.fetch(page, pageSize, search, statusParam);
        setLoading(false);
        if (success) {
            setFeedback(data.data.data);
            setCardDetail(data.counts)
            setPagination((prev) => ({
                ...prev,
                total: data.data.total,
                current: page,
                pageSize,
            }));
        }
    };

    useEffect(() => {
        changeDocumentTitle('Feedback')

        initialize(pagination.current, pagination.pageSize, search, statusFilter);
    }, [search, statusFilter]);

    const resolveModal = (
        <Modal
            title='Resolve feedback'
            open={showModal}
            onCancel={() => setShowModal(false)}
            footer={null}
        >
            {selectedRecord ?
                <div>
                    <p className='text-[#62748E] p-0 m-0 '> To, {selectedRecord?.email}[from, support@ctrade.us]</p >
                    <p className='text-sm mt-1'>Subject, Your feedback FB-{selectedRecord?.id} has been updated</p>
                </div>
                : null
            }
            <Form form={form} onFinish={onFinish}>
                <Form.Item name="resolved_message">
                    <Input.TextArea rows={7} />
                </Form.Item>
                <div className='flex gap-3 justify-end'>
                    <Button onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button type='primary' htmlType='submit' loading={loading}>
                        Send and Resolve
                    </Button>
                </div>
            </Form>
        </Modal >
    )

    const modal = (
        <Modal
            title={<p className='text-[#62748E]'> FB-{selectedRecord?.id || '0'} </p>}
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            footer={null}
        >
            {selectedRecord ?
                (
                    <div className="flex flex-col gap-3">
                        <div>
                            <p className='text-lg capitalize font-semibold p-0 m-0'>{selectedRecord.name}</p>
                            <p className='text-xs p-0 m-0'>{selectedRecord.email}</p>
                        </div>
                        <Rate value={selectedRecord.rating} disabled style={{ fontSize: 15 }} />
                        <p className='mt-1 text-sm font-light p-0 m-0 whitespace-pre-line'>{selectedRecord.feedback}</p>
                        {selectedRecord?.attachments?.length === 0 ? null : (
                            <div>
                                <p className="font-medium mb-2">Attachments</p>
                                <div className="flex flex-col gap-2">
                                    {selectedRecord.attachments?.map((att, idx) => (
                                        <div
                                            key={idx}
                                            className="flex justify-between items-center border p-2 rounded-md"
                                        >
                                            {/* Left side: thumbnail + info */}
                                            <div className="flex items-center gap-2">
                                                {isImage(att.filename) ? (
                                                    <img
                                                        src={att.url}
                                                        alt={att.filename}
                                                        className="w-12 h-12 object-cover rounded-md"
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded-md text-sm">
                                                        FILE
                                                    </div>
                                                )}
                                                <div className="flex flex-col">
                                                    <p className="text-sm font-medium truncate w-64">{att.filename}</p>
                                                    <p className="text-xs text-gray-500">{formatSize(att.size)}</p>
                                                </div>
                                            </div>

                                            {/* Right side: download */}
                                            <a
                                                href={att.url}
                                                download={att.filename}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <Button type="default" size="small"
                                                    onClick={() => {
                                                        const newTab = window.open(att.url, "_blank");
                                                        if (newTab) {
                                                            // Force download using `download` attribute
                                                            const link = newTab.document.createElement("a");
                                                            link.href = att.url;
                                                            link.download = att.filename;
                                                            newTab.document.body.appendChild(link);
                                                            link.click();
                                                            newTab.document.body.removeChild(link);
                                                        }
                                                    }}>
                                                    Download
                                                </Button>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                        }

                        <p className='text-[#62748E] text-xs'><span>Received,</span> {dayjs(selectedRecord.created_at).format("YYYY-MM-DD HH:mm")}</p>
                    </div>
                ) :
                null
                // (<p>Loading...</p>)
            }
        </Modal >
    )
    return (
        <div>
            <div className='flex gap-5'>
                {cardData.map((data, index) => (
                    <div className='p-3 bg-white w-full border-[#D9D9D9] border-2 rounded-xl'>
                        <h1 className='text-[12px] text-[#62748E]'>{data.title}</h1>
                        {loading ? (
                            <Spin active size="small" className="w-16 h-6" />
                        ) : (
                            <h2 className="text-3xl p-0 m-0">{data.count}</h2>
                        )}
                        {/* <h2 className="text-3xl p-0 m-0">{data.count}</h2> */}
                        {data.rating && <h2 className="text-[12px] p-0 m-0 text-[#62748E]">{data.rating}</h2>}
                    </div>
                ))}
            </div>

            <div className='flex border p-0 mt-8 bg-white items-center justify-between gap-4 px-6 rounded-xl'>
                <div className='flex gap-2'>
                    <Button className='rounded-2xl'
                        type={statusFilter === 'All' ? 'primary' : 'default'}
                        onClick={() => setStatusFilter('All')}
                    >All</Button>
                    <Button className='rounded-2xl'
                        type={statusFilter === FEEDBACK_STATUS.OPEN ? 'primary' : 'default'}
                        onClick={() => setStatusFilter(FEEDBACK_STATUS.OPEN)}
                    >Open</Button>
                    <Button className='rounded-2xl'
                        type={statusFilter === FEEDBACK_STATUS.RESOLVED ? 'primary' : 'default'}
                        onClick={() => setStatusFilter(FEEDBACK_STATUS.RESOLVED)}
                    >Resolved</Button>
                </div>
                <Input prefix={<SearchOutlined />} onChange={(e) => setSearch(e.target.value)} placeholder="Search by customer,email,id or text" style={{
                    width: "33%",
                    borderRadius: "20px",
                    fontSize: 14,
                }} className='my-4' />
            </div>
            <div className="customer1-table-wrapper mt-8">
                <Table
                    columns={columns}
                    loading={loading}
                    dataSource={feedback}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                    style={{ marginTop: "10px" }}
                    footer={() => (
                        <div className="flex justify-between items-center text-xs ">
                            <div className=" text-gray-600">
                                Showing {(pagination.current - 1) * pagination.pageSize + 1} to{" "}
                                {Math.min(pagination.current * pagination.pageSize, pagination.total)} of{" "}
                                {pagination.total} entries
                            </div>
                            <Pagination
                                current={pagination.current}
                                total={pagination.total}
                                pageSize={pagination.pageSize}
                                showSizeChanger={false}
                                onChange={(page) => initialize(page, pagination.pageSize, search, statusFilter)}
                                itemRender={(current, type, originalElement) => {
                                    if (type === "prev") return <a className="bg-[#EEEEEE] p-2 rounded-sm hover:text-primary">Previous</a>;
                                    if (type === "next") return <a className="bg-[#EEEEEE] p-2 rounded-sm hover:text-primary">Next</a>;
                                    return originalElement;
                                }}
                            />
                        </div>
                    )}
                />
            </div>
            {resolveModal}
            {modal}
        </div>

    )
}

export default AdminFeedback
